"use client";

import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useConfirmation } from "@/components/common/ConfirmationDialog";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  Plus,
  Trash2,
  Edit3,
  Percent,
  DollarSign,
  X,
  ChevronDown,
  Check,
  RefreshCw,
} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import {
  StandardFormInput,
  StandardFormSelect,
} from "@/components/common/StandardFormInput";
import { ActionButton } from "@/components/common/DataViewTypes";
import { ProductSelector } from "@/components/common/ProductSelector";
import { RootState, AppDispatch } from "@/store/store";
import {
  fetchTaxesRequest,
  createTaxRequest,
  updateTaxRequest,
  deleteTaxRequest,
  resetFormState,
} from "@/store/slices/tax.slice";
import { fetchChargesRequest } from "@/store/slices/charge.slice";
import { Tax, CreateTaxDto } from "@/types/models/tax";

interface AddTaxForm {
  name: string;
  description: string;
  tax_amount: string;
  tax_mode: "inclusive" | "exclusive" | "";
  tax_type: "percentage" | "fixed" | "";
  applicable_on_all_products: boolean;
  applicable_on_all_charges: boolean;
  applicable_products: string[];
  applicable_charges: string[];
}

const TaxesTab = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { confirm } = useConfirmation();

  const {
    items: taxes,
    loaded,
    loading,
    error,
    pagination,
    formState,
  } = useSelector((state: RootState) => state.tax);

  const {
    items: charges,
    loaded: chargesLoaded,
    loading: chargesLoading,
    error: chargesError,
  } = useSelector((state: RootState) => state.charges);
  const [pageIndex, setPageIndex] = useState(0);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTaxId, setEditingTaxId] = useState<string | null>(null);
  const [addForm, setAddForm] = useState<AddTaxForm>({
    name: "",
    description: "",
    tax_amount: "",
    tax_mode: "",
    tax_type: "",
    applicable_on_all_products: false,
    applicable_on_all_charges: false,
    applicable_products: [],
    applicable_charges: [],
  });

  const fetchTaxes = useCallback(() => {
    dispatch(fetchTaxesRequest({ page: pageIndex + 1 }));
  }, [dispatch, pageIndex]);

  // Load taxes on component mount / pagination with guarded fetches
  useEffect(() => {
    if (loading) return;

    const hasNotFoundError = error?.status === 404;
    if (hasNotFoundError) return;

    const currentPageIndex = Math.max(0, (pagination?.page || 1) - 1);
    const hasPageMismatch = currentPageIndex !== pageIndex;

    if (!loaded && taxes.length === 0) {
      fetchTaxes();
      return;
    }

    if (loaded && hasPageMismatch) {
      fetchTaxes();
    }
  }, [loaded, taxes.length, loading, error?.status, pagination?.page, pageIndex, fetchTaxes]);

  // Load charges only when modal is opened
  useEffect(() => {
    const hasNotFoundError = chargesError?.status === 404;
    if (!isAddModalOpen || chargesLoading || hasNotFoundError) return;

    if (!chargesLoaded && charges.length === 0) {
      dispatch(fetchChargesRequest({}));
    }
  }, [isAddModalOpen, dispatch, charges.length, chargesLoaded, chargesLoading, chargesError?.status]);

  // Reset form state when modal closes
  useEffect(() => {
    if (!isAddModalOpen) {
      dispatch(resetFormState());
      setIsEditMode(false);
      setEditingTaxId(null);
    }
  }, [isAddModalOpen, dispatch]);

  // Close modal and reset form when tax is successfully created
  useEffect(() => {
    if (formState.success && !formState.loading) {
      resetForm();
      setIsAddModalOpen(false);
    }
  }, [formState.success, formState.loading]);

  const taxModes = [
    {
      value: "inclusive",
      label: "Inclusive",
      description: "Tax included in price",
    },
    {
      value: "exclusive",
      label: "Exclusive",
      description: "Tax added to price",
    },
  ];

  const taxTypes = [
    {
      value: "percentage",
      label: "Percentage",
      icon: <Percent className="h-4 w-4" />,
      description: "Percentage-based tax calculation",
    },
    {
      value: "fixed",
      label: "Fixed Amount",
      icon: <DollarSign className="h-4 w-4" />,
      description: "Fixed amount tax",
    },
  ];

  const getTypeIcon = (type: Tax["tax_type"] | string | undefined) => {
    const typeConfig: Record<string, { icon: React.ReactNode; color: string }> =
      {
        percentage: {
          icon: <Percent className="h-4 w-4" />,
          color: "text-blue-600",
        },
        fixed: {
          icon: <DollarSign className="h-4 w-4" />,
          color: "text-green-600",
        },
      };

    const cfg = type ? typeConfig[type] : undefined;

    // Fallback when type is unknown/undefined to avoid runtime errors
    const safeCfg = cfg ?? {
      icon: <Percent className="h-4 w-4" />,
      color: "text-gray-600",
    };

    return <div className={`${safeCfg.color}`}>{safeCfg.icon}</div>;
  };

  const getTypeBadge = (type: Tax["tax_type"] | string | undefined) => {
    const typeColors: Record<string, string> = {
      percentage: "bg-blue-500/20 text-blue-600 hover:bg-blue-500/20",
      fixed: "bg-green-500/20 text-green-600 hover:bg-green-500/20",
    };

    const cls = type ? typeColors[type] : undefined;

    return (
      <Badge className={cls ?? "bg-gray-200 text-gray-600"}>
        {type === "percentage"
          ? "Percentage"
          : type === "fixed"
            ? "Fixed"
            : "N/A"}
      </Badge>
    );
  };

  const handleAddTax = () => {
    if (
      addForm.name &&
      addForm.description &&
      addForm.tax_amount &&
      addForm.tax_mode &&
      addForm.tax_type
    ) {
      const taxData: CreateTaxDto = {
        name: addForm.name,
        description: addForm.description,
        tax_amount: parseFloat(addForm.tax_amount),
        tax_mode: addForm.tax_mode as "inclusive" | "exclusive",
        tax_type: addForm.tax_type as "percentage" | "fixed",
        enabled: true,
        applicable_on_all_products: addForm.applicable_on_all_products,
        applicable_on_all_charges: addForm.applicable_on_all_charges,
        applicable_products: addForm.applicable_on_all_products
          ? undefined
          : addForm.applicable_products,
        applicable_charges: addForm.applicable_on_all_charges
          ? undefined
          : addForm.applicable_charges,
      };

      if (isEditMode && editingTaxId) {
        dispatch(updateTaxRequest({ id: editingTaxId, data: taxData }));
      } else {
        dispatch(createTaxRequest(taxData));
      }
    }
  };

  const resetForm = () => {
    setAddForm({
      name: "",
      description: "",
      tax_amount: "",
      tax_mode: "",
      tax_type: "",
      applicable_on_all_products: false,
      applicable_on_all_charges: false,
      applicable_products: [],
      applicable_charges: [],
    });
  };

  const handleEditTax = (tax: Tax) => {
    setAddForm({
      name: tax.name,
      description: tax.description,
      tax_amount: tax.tax_amount.toString(),
      tax_mode: tax.tax_mode,
      tax_type: tax.tax_type,
      applicable_on_all_products: tax.applicable_on_all_products,
      applicable_on_all_charges: tax.applicable_on_all_charges,
      applicable_products: tax.applicable_products || [],
      applicable_charges: tax.applicable_charges || [],
    });
    setIsEditMode(true);
    setEditingTaxId(tax.id);
    setIsAddModalOpen(true);
  };

  const handleRemoveTax = async (id: string) => {
    const result = await confirm({
      title: "Delete Tax",
      message:
        "Are you sure you want to delete this tax? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "destructive",
    });

    if (result.confirmed) {
      dispatch(deleteTaxRequest({ id }));
    }
  };

  const columns: ColumnDef<Tax>[] = [
    {
      accessorKey: "name",
      header: "Tax",
      size: 350,
      cell: ({ row }) => {
        const tax = row.original;
        return (
          <div className="flex items-center gap-3 min-w-[300px]">
            <div className="h-10 w-10 bg-muted/50 rounded-lg flex items-center justify-center">
              {getTypeIcon(tax.tax_type)}
            </div>
            <div>
              <p className="font-medium text-foreground">{tax.name}</p>
              <div className="text-xs text-muted-foreground">
                {tax.description}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "tax_type",
      header: "Type",
      cell: ({ row }) => {
        return getTypeBadge(row.original.tax_type);
      },
    },
    {
      accessorKey: "tax_amount",
      header: "Amount",
      cell: ({ row }) => {
        const tax = row.original;
        return (
          <div className="font-medium">
            {tax.tax_amount}
            {tax.tax_type === "percentage" ? "%" : " ₹"}
          </div>
        );
      },
    },
    {
      accessorKey: "tax_mode",
      header: "Application",
      cell: ({ row }) => {
        const tax = row.original;
        return (
          <Badge
            className={
              tax.tax_mode === "inclusive"
                ? "bg-green-500/20 text-green-600 hover:bg-green-500/20"
                : "bg-orange-500/20 text-orange-600 hover:bg-orange-500/20"
            }
          >
            {tax.tax_mode}
          </Badge>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      size: 140,
      cell: ({ row }) => {
        const raw = (row.original as any).created_at;
        const date = raw ? new Date(raw) : null;
        const text =
          date && !isNaN(date.getTime()) ? format(date, "PP p") : "-";
        return <div className="text-sm text-muted-foreground">{text}</div>;
      },
    },
    {
      accessorKey: "updated_at",
      header: "Last Updated",
      size: 140,
      cell: ({ row }) => {
        const raw = (row.original as any).updated_at;
        const date = raw ? new Date(raw) : null;
        const text =
          date && !isNaN(date.getTime()) ? format(date, "PP p") : "-";
        return <div className="text-sm text-muted-foreground">{text}</div>;
      },
    },
    {
      id: "actions",
      header: "Actions",
      size: 120,
      cell: ({ row }) => {
        const tax = row.original;
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleEditTax(tax)}
            >
              <Edit3 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
              onClick={() => handleRemoveTax(tax.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  // Create action buttons
  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />,
      onClick: fetchTaxes,
      variant: "outline",
      disabled: loading,
    },
    {
      label: "Add Tax",
      icon: <Plus className="h-3 w-3" />,
      onClick: () => setIsAddModalOpen(true),
      variant: "primary",
    },
  ];

  return (
    <div>
      <TableContainer>
        <div className="bg-secondary-background rounded-lg p-4">
          <DataView
            title="Taxes"
            description="Configure tax rates and rules for your products and services"
            columns={columns}
            data={taxes}
            loading={loading}
            pagination={pagination}
            pageIndex={pageIndex}
            onPageChange={setPageIndex}
            emptyMessage="No taxes found. Add your first tax to get started."
            actionButtons={actionButtons}
          />
        </div>
      </TableContainer>

      {/* Add Tax Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto bg-secondary-background">
          <DialogHeader>
            <DialogTitle>{isEditMode ? "Edit Tax" : "Add New Tax"}</DialogTitle>
            <DialogDescription>
              {isEditMode
                ? "Update the tax configuration."
                : "Create a tax that can be applied to products and charges."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            {/* Show form-level errors */}
            {formState.error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">
                  {formState.error.message}
                </p>
              </div>
            )}
            {/* Tax Name */}
            <StandardFormInput
              label="Tax Name"
              placeholder="e.g., GST, Service Tax, VAT"
              value={addForm.name}
              onChange={(e) =>
                setAddForm((prev) => ({ ...prev, name: e.target.value }))
              }
            />

            {/* Tax Description */}
            <StandardFormInput
              label="Description"
              placeholder="Enter tax description"
              value={addForm.description}
              onChange={(e) =>
                setAddForm((prev) => ({ ...prev, description: e.target.value }))
              }
            />

            {/* Tax Amount */}
            <StandardFormInput
              label="Tax Amount"
              type="number"
              step="0.01"
              placeholder="Enter amount"
              value={addForm.tax_amount}
              onChange={(e) =>
                setAddForm((prev) => ({ ...prev, tax_amount: e.target.value }))
              }
            />

            {/* Tax Mode Selection */}
            <StandardFormSelect
              label="Tax Mode"
              value={addForm.tax_mode}
              onValueChange={(value) =>
                setAddForm((prev) => ({
                  ...prev,
                  tax_mode: value as Tax["tax_mode"],
                }))
              }
              options={taxModes.map((mode) => ({
                label: `${mode.label} - ${mode.description}`,
                value: mode.value,
              }))}
              placeholder="Select tax application mode"
            />

            {/* Tax Type Selection */}
            <StandardFormSelect
              label="Tax Type"
              value={addForm.tax_type}
              onValueChange={(value) =>
                setAddForm((prev) => ({
                  ...prev,
                  tax_type: value as Tax["tax_type"],
                }))
              }
              options={taxTypes.map((type) => ({
                label: `${type.label} - ${type.description}`,
                value: type.value,
              }))}
              placeholder="Select tax calculation type"
            />

            {/* Toggle Options */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 bg-background rounded-lg">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">All Products</Label>
                  <p className="text-xs text-muted-foreground">
                    {addForm.applicable_on_all_products
                      ? "Currently applying to all products"
                      : "Currently applying to selected products"}
                  </p>
                </div>
                <Switch
                  checked={addForm.applicable_on_all_products}
                  onCheckedChange={(checked) =>
                    setAddForm((prev) => ({
                      ...prev,
                      applicable_on_all_products: checked,
                      applicable_products: checked
                        ? []
                        : prev.applicable_products,
                    }))
                  }
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-background rounded-lg">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">All Charges</Label>
                  <p className="text-xs text-muted-foreground">
                    {addForm.applicable_on_all_charges
                      ? "Currently applying to all charges"
                      : "Currently applying to selected charges"}
                  </p>
                </div>
                <Switch
                  checked={addForm.applicable_on_all_charges}
                  onCheckedChange={(checked) =>
                    setAddForm((prev) => ({
                      ...prev,
                      applicable_on_all_charges: checked,
                      applicable_charges: checked
                        ? []
                        : prev.applicable_charges,
                    }))
                  }
                />
              </div>
            </div>

            {/* Specific Products Selection */}
            {!addForm.applicable_on_all_products && (
              <ProductSelector
                label="Specific Products"
                value={addForm.applicable_products}
                onValueChange={(value) =>
                  setAddForm((prev) => ({
                    ...prev,
                    applicable_products: value,
                  }))
                }
                placeholder="Select products to apply tax"
                multiSelect={true}
              />
            )}

            {/* Specific Charges Selection */}
            {!addForm.applicable_on_all_charges && (
              <div className="space-y-1">
                <label className="block text-[13px] font-normal text-muted-foreground">
                  Specific Charges
                </label>
                <div className="bg-background border border-border rounded-xl overflow-hidden">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-full h-auto min-h-[38px] justify-between bg-transparent hover:bg-transparent px-4 py-2 font-normal"
                      >
                        <div className="flex-1 text-left">
                          {addForm.applicable_charges.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {addForm.applicable_charges
                                .slice(0, 3)
                                .map((chargeId) => {
                                  const charge = charges.find(
                                    (c) => c.id === chargeId,
                                  );
                                  return (
                                    <span
                                      key={chargeId}
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-md"
                                    >
                                      {charge?.name}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setAddForm((prev) => ({
                                            ...prev,
                                            applicable_charges:
                                              prev.applicable_charges.filter(
                                                (c) => c !== chargeId,
                                              ),
                                          }));
                                        }}
                                        className="ml-1 text-primary/60 hover:text-primary text-xs"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  );
                                })}
                              {addForm.applicable_charges.length > 3 && (
                                <span className="text-xs text-muted-foreground px-1.5 py-1">
                                  +{addForm.applicable_charges.length - 3} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">
                              Select charges to apply tax
                            </span>
                          )}
                        </div>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="p-1 bg-secondary-background"
                      align="start"
                    >
                      <div className="max-h-[200px] overflow-y-auto">
                        {charges.map((charge) => {
                          const isSelected =
                            addForm.applicable_charges.includes(charge.id);
                          return (
                            <div
                              key={charge.id}
                              className={cn(
                                "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground",
                                isSelected &&
                                  "bg-accent text-accent-foreground",
                              )}
                              onClick={() => {
                                if (isSelected) {
                                  setAddForm((prev) => ({
                                    ...prev,
                                    applicable_charges:
                                      prev.applicable_charges.filter(
                                        (c) => c !== charge.id,
                                      ),
                                  }));
                                } else {
                                  setAddForm((prev) => ({
                                    ...prev,
                                    applicable_charges: [
                                      ...prev.applicable_charges,
                                      charge.id,
                                    ],
                                  }));
                                }
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4 text-primary",
                                  isSelected ? "opacity-100" : "opacity-0",
                                )}
                              />
                              <div className="flex-1">
                                <p className="font-medium">{charge.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {charge.description}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                setIsAddModalOpen(false);
              }}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleAddTax}
              disabled={
                !addForm.name ||
                !addForm.description ||
                !addForm.tax_amount ||
                !addForm.tax_mode ||
                !addForm.tax_type ||
                formState.loading
              }
            >
              <Plus className="h-4 w-4 mr-2" />
              {formState.loading
                ? "Saving..."
                : isEditMode
                  ? "Update Tax"
                  : "Add Tax"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaxesTab;
